import { useState, useEffect } from 'react';
import { X, Check, Megaphone } from 'lucide-react';
import { AdTier, getAdPricing, createAdCheckoutSession, AdPricingInfo, AdPlacementRequest } from '../services/api';
import { useAuth } from '@clerk/clerk-react';

interface AdPlacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
}

const AD_TIER_CONFIG = [
  {
    id: 'BRONZE_INLINE' as AdTier,
    name: 'Bronze Inline',
    description: 'Inline ad placement in search results',
    impressions: '5K-10K impressions/month',
    features: [
      'Inline placement in search results',
      'Basic targeting options',
      'Monthly reporting',
    ],
  },
  {
    id: 'SILVER_SPONSORED' as AdTier,
    name: 'Silver Sponsored',
    description: 'Sponsored placement in search results',
    impressions: '15K-25K impressions/month',
    features: [
      'Sponsored placement in search results',
      'Enhanced targeting options',
      'Priority placement',
      'Monthly reporting & analytics',
    ],
    popular: true,
  },
  {
    id: 'GOLD_FEATURED' as AdTier,
    name: 'Gold Featured',
    description: 'Featured placement in search results',
    impressions: '30K-50K impressions/month',
    features: [
      'Featured placement in search results',
      'Advanced targeting options',
      'Top placement priority',
      'Detailed analytics & reporting',
      'Dedicated support',
    ],
  },
  {
    id: 'PLATINUM_FEATURED' as AdTier,
    name: 'Platinum Featured',
    description: 'Premium featured placement at the top of search results',
    impressions: '75K-100K+ impressions/month',
    features: [
      'Premium top placement',
      'Full targeting control',
      'Highest visibility',
      'Comprehensive analytics',
      'Priority support',
      'Custom ad formats',
    ],
  },
];

export function AdPlacementModal({ isOpen, onClose, loading }: AdPlacementModalProps) {
  const { user, getToken } = useAuth();
  const [pricing, setPricing] = useState<Record<string, AdPricingInfo> | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<AdTier | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    advertiser_name: user?.fullName || '',
    advertiser_email: user?.primaryEmailAddress?.emailAddress || '',
  });

  useEffect(() => {
    if (isOpen) {
      loadPricing();
      setPurchaseError(null);
      setShowForm(false);
      setSelectedTier(null);
    }
  }, [isOpen]);

  const loadPricing = async () => {
    setPricingLoading(true);
    setPricingError(null);
    try {
      const response = await getAdPricing();
      setPricing(response.pricing);
    } catch (error) {
      console.error('Error loading ad pricing:', error);
      setPricingError(error instanceof Error ? error.message : 'Failed to load ad pricing');
    } finally {
      setPricingLoading(false);
    }
  };

  const handlePurchase = async (tier: AdTier) => {
    setSelectedTier(tier);
    setShowForm(true);
    setPurchaseError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTier) return;

    setPurchaseError(null);
    
    try {
      const request: AdPlacementRequest = {
        tier: selectedTier,
        title: formData.title,
        description: formData.description || undefined,
        image_url: formData.image_url || undefined,
        link_url: formData.link_url,
        advertiser_name: formData.advertiser_name,
        advertiser_email: formData.advertiser_email,
        success_url: `${window.location.origin}?ad_placement=success`,
        cancel_url: `${window.location.origin}?ad_placement=cancelled`,
      };

      const session = await createAdCheckoutSession(request, getToken);
      // Redirect to Stripe checkout
      window.location.href = session.url;
    } catch (error) {
      console.error('Error creating ad checkout:', error);
      setPurchaseError(error instanceof Error ? error.message : 'Failed to start checkout. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Ad Placement Options</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Promote your products with targeted advertising</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-neutral-600 dark:text-neutral-400" />
          </button>
        </div>

        <div className="p-6">
          {purchaseError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="text-red-800 dark:text-red-200 text-sm font-medium">{purchaseError}</div>
            </div>
          )}

          {showForm && selectedTier ? (
            <div>
              <div className="mb-6">
                <button
                  onClick={() => {
                    setShowForm(false);
                    setSelectedTier(null);
                  }}
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                >
                  ← Back to ad tiers
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="ad-title" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Ad Title *
                  </label>
                  <input
                    id="ad-title"
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    placeholder="Enter ad title"
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor="ad-description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Description
                  </label>
                  <textarea
                    id="ad-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    rows={3}
                    placeholder="Enter ad description"
                  />
                </div>
                <div>
                  <label htmlFor="ad-image-url" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Image URL
                  </label>
                  <input
                    id="ad-image-url"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label htmlFor="ad-link-url" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Link URL *
                  </label>
                  <input
                    id="ad-link-url"
                    type="url"
                    required
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    placeholder="https://example.com"
                    aria-required="true"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="advertiser-name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Advertiser Name *
                    </label>
                    <input
                      id="advertiser-name"
                      type="text"
                      required
                      value={formData.advertiser_name}
                      onChange={(e) => setFormData({ ...formData, advertiser_name: e.target.value })}
                      className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label htmlFor="advertiser-email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Email *
                    </label>
                    <input
                      id="advertiser-email"
                      type="email"
                      required
                      value={formData.advertiser_email}
                      onChange={(e) => setFormData({ ...formData, advertiser_email: e.target.value })}
                      className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      aria-required="true"
                    />
                  </div>
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-6 rounded-lg bg-accent-600 hover:bg-accent-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : 'Continue to Checkout'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              {pricingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-neutral-500 dark:text-neutral-400">Loading ad pricing...</div>
                </div>
              ) : pricingError ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-red-500 dark:text-red-400">Error loading pricing: {pricingError}</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {AD_TIER_CONFIG.map((tier) => {
                    const tierPricing = pricing?.[tier.id];
                    const displayPrice = tierPricing?.configured && tierPricing?.formatted_price !== 'N/A' 
                      ? tierPricing.formatted_price 
                      : 'N/A';
                    const displayPeriod = tierPricing?.period ? `/${tierPricing.period}` : '/month';

                    return (
                      <div
                        key={tier.id}
                        className={`relative rounded-xl border-2 p-6 transition-all ${
                          tier.popular
                            ? 'border-accent-600 dark:border-accent-500 bg-accent-50/50 dark:bg-accent-900/20'
                            : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800'
                        }`}
                      >
                        {tier.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-accent-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                              Popular
                            </span>
                          </div>
                        )}

                        <div className="text-center mb-6">
                          <div className="flex justify-center mb-2">
                            <Megaphone size={24} className="text-accent-600 dark:text-accent-400" />
                          </div>
                          <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{tier.name}</h3>
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">{displayPrice}</span>
                            <span className="text-neutral-500 dark:text-neutral-400">{displayPeriod}</span>
                          </div>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">{tier.description}</p>
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">{tier.impressions}</p>
                        </div>

                        <ul className="space-y-2 mb-6">
                          {tier.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check size={16} className="text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-neutral-700 dark:text-neutral-300">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handlePurchase(tier.id)}
                          disabled={!tierPricing?.configured}
                          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                            tier.popular
                              ? 'bg-accent-600 hover:bg-accent-700 text-white'
                              : 'bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-200'
                          } ${!tierPricing?.configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {tierPricing?.configured ? 'Purchase Ad Spot' : 'Not Available'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

